import { Company, DocumentStatus } from '@neo.tax/shared/graphql';
import { useCurrentUserScopeForCompany, useGetLatestFederalDocumentStatusesForCompany } from '@neo.tax/shared/hooks';

import { DocumentStatusBadge } from '../document-status-badge';

interface CompanyStatusProps {
  company: Pick<Company, 'id' | 'isPiiRedacted'>;
}

const calculateStatus = ({
  taxCreditFormStatus,
  taxWriteUpStatus,
}: {
  taxWriteUpStatus?: DocumentStatus;
  taxCreditFormStatus?: DocumentStatus;
}): DocumentStatus | null => {
  const documentStatuses = [taxWriteUpStatus, taxCreditFormStatus];
  if (documentStatuses.includes(DocumentStatus.ReadyForDownload)) {
    return DocumentStatus.ReadyForDownload;
  } else if (documentStatuses.includes(DocumentStatus.HumanReview)) {
    return DocumentStatus.HumanReview;
  } else if (documentStatuses.includes(DocumentStatus.ReadyToSubmit)) {
    return DocumentStatus.ReadyToSubmit;
  } else if (documentStatuses.includes(DocumentStatus.MissingInformation)) {
    return DocumentStatus.MissingInformation;
  }
  return null;
};

export const CompanyStatus = ({ company }: CompanyStatusProps): React.ReactElement | null => {
  const { limitAccessToFinancialData } = useCurrentUserScopeForCompany(company);
  const { documentStatuses, loading } = useGetLatestFederalDocumentStatusesForCompany({
    skip: limitAccessToFinancialData,
    companyId: company.id,
  });

  // If done loading but no document statuses available, show fallback
  if (!loading && !documentStatuses) {
    return <span>--</span>;
  }

  const companyStatus = calculateStatus({
    taxWriteUpStatus: documentStatuses?.taxWriteUpStatus,
    taxCreditFormStatus: documentStatuses?.taxCreditFormStatus,
  });
  return <DocumentStatusBadge status={companyStatus} />;
};
